import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationService } from '@alfresco/adf-core';
import { environment } from '../../../../environments/environment';
import { AppConfigService } from '@alfresco/adf-core';
// ==================== INTERFACES ====================

export interface ContractTemplate {
  id: string;
  name: string;
  description?: string;
  title?: string;
  contractType?: string;
  mimeType?: string;
  sizeInBytes?: number;
  createdAt?: string;
  modifiedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
  path?: string;
}

export interface ContractData {
  contractTitle: string;
  contractType: string;
  contractNumber?: string;
  supplierName: string;
  departmentName: string;
  companyName?: string;
  contractAmount?: number;
  currency?: string;
  amountInWords?: string;
  signingDate?: string;
  effectiveDate?: string;
  expiryDate?: string;
  isRenewable?: boolean;
  autoRenew?: boolean;
  renewalTermMonths?: number;
  vendorId?: string;
  vendorEmail?: string;
  vendorPhone?: string;
  vendorAddress?: string;
  vendorContactPerson?: string;
  vendorRegistrationNumber?: string;
  vendorTaxId?: string;
  bankName?: string;
  bankBranch?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  swiftCode?: string;
  priority?: string;
  confidential?: boolean;
  internalNotes?: string;
  contractStatus?: string;
  // Type-specific fields
  deliveryTerms?: string;
  paymentTerms?: string;
  warrantyPeriod?: number;
  serviceDescription?: string;
  slaTerms?: string;
  confidentialityPeriod?: number;
  disclosingParty?: string;
  receivingParty?: string;
  propertyAddress?: string;
  leaseTermMonths?: number;
  securityDeposit?: number;
  jobTitle?: string;
  probationPeriod?: number;
  noticePeriod?: number;
  benefits?: string;
  maintenanceSchedule?: string;
  responseTime?: number;
  coverageScope?: string;
  licenseType?: string;
  numberOfUsers?: number;
  territory?: string;
  restrictions?: string;
}

export interface ReviewerApprover {
  id: string;
  displayName: string;
  email?: string;
  order: number;
  signatureType?: 'digital' | 'manual' | 'none';
  status?: string;
  actionDate?: string;
  comments?: string;
}

export interface ReminderConfig {
  id?: number;
  reminderType: 'EXPIRY' | 'RENEWAL' | 'REVIEW' | 'PAYMENT' | 'CUSTOM';
  purpose: string;
  reminderDate: string;
  daysBefore?: number;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  notifyOwner: boolean;
  notifyReviewers: boolean;
  notifyApprovers: boolean;
  notifyDepartment: boolean;
  recipients?: string[];
  sent?: boolean;
  sentAt?: string;
}

export interface TemplateFillRequest {
  templateNodeId: string;
  targetFolderId?: string;
  contractData: ContractData;
  customPlaceholders?: { [key: string]: string };
  reviewers?: ReviewerApprover[];
  approvers?: ReviewerApprover[];
  reminders?: ReminderConfig[];
  linkedContracts?: any[];
}

export interface TemplateFillResponse {
  success: boolean;
  errorMessage?: string;
  contractNodeId?: string;
  contractNumber?: string;
  documentName?: string;
  createdAt?: string;
  replacedPlaceholders?: string[];
  missingPlaceholders?: string[];
  nodeProperties?: { [key: string]: any };
}

export interface ContractDetails {
  id: string;
  name: string;
  contractTitle?: string;
  contractNumber?: string;
  contractType?: string;
  contractStatus?: string;
  /**
   * WORKFLOW — entered via contractReviewApproval BPMN
   * EXECUTED  — uploaded directly as a signed contract (no workflow)
   * Populated by ContractWorkflowState.contractOrigin via the GET /api/contracts/{id} endpoint.
   */
  contractOrigin?: string;
  supplierName?: string;
  departmentName?: string;
  contractAmount?: number;
  currency?: string;
  effectiveDate?: string;
  expiryDate?: string;
  priority?: string;
  createdAt?: string;
  modifiedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
  path?: string;
  mimeType?: string;
  sizeInBytes?: number;
  metadata?: { [key: string]: any };
  viewUrl?: string;
  downloadUrl?: string;
  downloadPdfUrl?: string;
  reviewers?: Array<{ id: string; displayName: string; email: string; order: number; status: string; signatureType?: string; }>;
  approvers?: Array<{ id: string; displayName: string; email: string; order: number; status: string; signatureType?: string; }>;
}

export interface NotificationRecipient {
  userId?: string;
  displayName?: string;
  email: string;
  role?: 'OWNER' | 'REVIEWER' | 'APPROVER' | 'DEPARTMENT_HEAD' | 'CUSTOM';
}

export interface ReminderSummary {
  totalPending: number;
  dueToday: number;
  overdue: number;
  byType: { [key: string]: number };
}

export interface ExpiryDashboard {
  expired: number;
  critical: number;
  warning: number;
  notice: number;
}

/**
 * Angular Service for Contract Management API v2.1
 *
 * New in v2.1:
 *   - uploadContractWithMetadata() — multipart DOCX upload with full metadata
 *   - searchAllContracts(term)     — free-text contract search
 */
@Injectable({
  providedIn: 'root'
})
export class ContractService {

  private apiUrl: string;
  private reminderApiUrl: string;
  // private apiUrl = 'http://localhost:9090/api/contracts';
  // private reminderApiUrl = 'http://localhost:9090/api/reminders';
  //private alfrescoBase = environment.alfrescoUrl;

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private authService: AuthenticationService
  ) {
    const base = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.apiUrl = `${base}/contracts`;
    this.reminderApiUrl = `${base}/reminders`;
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  private getAuthHeadersOnly(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({ 'Authorization': `Bearer ${token}` });
  }

  // ==================== TEMPLATE ENDPOINTS ====================

  getTemplates(): Observable<ContractTemplate[]> {
    return this.http.get<any>(`${this.apiUrl}/templates`, { headers: this.getHeaders() })
      .pipe(
        map(response => response.templates || []),
        catchError(this.handleError)
      );
  }

  getTemplate(templateId: string): Observable<ContractTemplate> {
    return this.http.get<any>(`${this.apiUrl}/templates/${templateId}`, { headers: this.getHeaders() })
      .pipe(
        map(response => response.template),
        catchError(this.handleError)
      );
  }

  downloadTemplate(templateId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/templates/${templateId}/download`, {
      headers: this.getAuthHeadersOnly(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // ==================== CONTRACT CREATION ====================

  /**
   * Fill a template with contract data and save to Alfresco.
   */
  fillTemplate(request: TemplateFillRequest): Observable<TemplateFillResponse> {
    return this.http.post<TemplateFillResponse>(`${this.apiUrl}/fill-template`, request, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  /**
   * Upload a raw DOCX file (not template-based) with full metadata.
   *
   * @param file           The DOCX File object from <input type="file">
   * @param contractData   Contract metadata (contractTitle, type, supplier, dates, etc.)
   * @param reviewers      Reviewer list
   * @param approvers      Approver list
   * @param reminders      Reminder list
   * @param linkedContracts Linked contracts array
   * @param sendForReview  If true, contractStatus will be 'Pending Review' instead of 'Draft'
   */
  uploadContractWithMetadata(
    file: File,
    contractData: ContractData,
    reviewers: ReviewerApprover[] = [],
    approvers: ReviewerApprover[] = [],
    reminders: ReminderConfig[] = [],
    linkedContracts: any[] = [],
    sendForReview: boolean = false
  ): Observable<TemplateFillResponse> {

    const metadata = {
      contractTitle: contractData.contractTitle,
      contractType: contractData.contractType || 'Other',
      contractNumber: contractData.contractNumber,
      contractStatus: sendForReview ? 'Pending Review' : 'Draft',
      supplierName: contractData.supplierName,
      departmentName: contractData.departmentName,
      companyName: contractData.companyName || 'SkyPlus Limited',
      contractAmount: contractData.contractAmount,
      currency: contractData.currency || 'KES',
      effectiveDate: contractData.effectiveDate,
      expiryDate: contractData.expiryDate,
      isRenewable: contractData.isRenewable,
      priority: contractData.priority || 'Medium',
      internalNotes: contractData.internalNotes,
      // Type-specific fields
      deliveryTerms: contractData.deliveryTerms,
      paymentTerms: contractData.paymentTerms,
      warrantyPeriod: contractData.warrantyPeriod,
      serviceDescription: contractData.serviceDescription,
      slaTerms: contractData.slaTerms,
      confidentialityPeriod: contractData.confidentialityPeriod,
      disclosingParty: contractData.disclosingParty,
      receivingParty: contractData.receivingParty,
      propertyAddress: contractData.propertyAddress,
      leaseTermMonths: contractData.leaseTermMonths,
      securityDeposit: contractData.securityDeposit,
      jobTitle: contractData.jobTitle,
      probationPeriod: contractData.probationPeriod,
      noticePeriod: contractData.noticePeriod,
      benefits: contractData.benefits,
      maintenanceSchedule: contractData.maintenanceSchedule,
      responseTime: contractData.responseTime,
      coverageScope: contractData.coverageScope,
      licenseType: contractData.licenseType,
      numberOfUsers: contractData.numberOfUsers,
      territory: contractData.territory,
      restrictions: contractData.restrictions,
      // Workflow
      reviewers: reviewers,
      approvers: approvers,
      reminders: reminders,
      linkedContracts: linkedContracts.map(c => ({
        nodeId: c.id,
        contractNumber: c.contractNumber,
        linkType: c.linkType || 'RELATED'
      })),
      convertToContractType: true
    };

    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('metadata', JSON.stringify(metadata));

    return this.http.post<TemplateFillResponse>(
      `${this.apiUrl}/upload`,
      formData,
      { headers: this.getAuthHeadersOnly() }  // No Content-Type — browser sets multipart boundary
    ).pipe(catchError(this.handleError));
  }

  previewContract(request: TemplateFillRequest, format: 'pdf' | 'docx' = 'pdf'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    return this.http.post(`${this.apiUrl}/preview`, request, {
      headers: this.getHeaders(),
      params: params,
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  generateContractNumber(contractType: string = 'Other'): Observable<{ contractNumber: string; contractType: string; year: number }> {
    const params = new HttpParams().set('contractType', contractType);
    return this.http.post<any>(`${this.apiUrl}/generate-number`, null, {
      headers: this.getHeaders(),
      params: params
    }).pipe(catchError(this.handleError));
  }

  getSequenceInfo(contractType: string = 'Other'): Observable<{ lastSequence: number; nextSequence: number }> {
    const params = new HttpParams().set('contractType', contractType);
    return this.http.get<any>(`${this.apiUrl}/sequence-info`, {
      headers: this.getHeaders(),
      params: params
    }).pipe(catchError(this.handleError));
  }

  // ==================== CONTRACT RETRIEVAL ====================

  getContract(contractId: string): Observable<ContractDetails> {
    return this.http.get<any>(`${this.apiUrl}/${contractId}`, { headers: this.getHeaders() })
      .pipe(
        map(response => response.contract || response),
        catchError(this.handleError)
      );
  }

  getContracts(page: number = 0, size: number = 100): Observable<any> {
    const params = new HttpParams()
      .set('skipCount', page.toString())
      .set('maxItems', size.toString());
    return this.http.get<any>(`${this.apiUrl}`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => response.contracts || response || []),
      catchError(this.handleError)
    );
  }

  getAllContracts(skipCount: number = 0, maxItems: number = 100): Observable<any> {
    return this.getContracts(skipCount, maxItems);
  }

  /**
   * Structured search by contract type, status, supplier, department.
   */
  searchContracts(criteria: {
    contractType?: string;
    status?: string;
    supplierName?: string;
    departmentName?: string;
  }): Observable<any[]> {
    let params = new HttpParams();
    if (criteria.contractType) params = params.set('contractType', criteria.contractType);
    if (criteria.status) params = params.set('status', criteria.status);
    if (criteria.supplierName) params = params.set('supplierName', criteria.supplierName);
    if (criteria.departmentName) params = params.set('departmentName', criteria.departmentName);
    return this.http.get<any>(`${this.apiUrl}/search`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => response.contracts || response || []),
      catchError(err => {
        console.error('Error searching contracts:', err);
        return of([]);
      })
    );
  }

  /**
   * Free-text search across contract number, title, and supplier name.
   * New in v2.1 — used for contract linking in the wizard.
   */
  searchAllContracts(term: string): Observable<any[]> {
    const params = new HttpParams().set('term', term);
    return this.http.get<any>(`${this.apiUrl}/search`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => response.contracts || response || []),
      catchError(err => {
        console.error('Error in free-text contract search:', err);
        return of([]);
      })
    );
  }

  // ==================== CONTRACT DOWNLOAD/VIEW ====================

  downloadContract(contractId: string, format: 'docx' | 'pdf' = 'docx'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    return this.http.get(`${this.apiUrl}/${contractId}/download`, {
      headers: this.getAuthHeadersOnly(),
      params: params,
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  viewContract(contractId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${contractId}/view`, {
      headers: this.getAuthHeadersOnly(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  viewContractPdf(contractId: string): Observable<Blob> {
    return this.viewContract(contractId);
  }

  getContractViewUrl(contractId: string): string {
    const token = this.authService.getToken();
    return `${this.apiUrl}/${contractId}/view?access_token=${token}`;
  }

  // ==================== USERS ====================

  getUsers(): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/users`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => response.users || response || []),
      catchError(err => {
        console.error('Error fetching users:', err);
        return of([]);
      })
    );
  }

  searchUsers(term: string): Observable<any[]> {
    // Backend endpoint: GET /api/contracts/users/search?term=
    const params = new HttpParams().set('term', term);
    return this.http.get<any>(`${this.apiUrl}/users/search`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => response.users || response || []),
      catchError(err => {
        console.error('Error searching users:', err);
        return of([]);
      })
    );
  }

  // ==================== DELETE ====================

  deleteContract(contractId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${contractId}`, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  /**
   * Update Alfresco custom properties for a contract.
   * Maps to PUT /api/contracts/{id}/metadata
   */
  updateContractMetadata(contractId: string, payload: Record<string, any>): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/${contractId}/metadata`,
      payload,
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Quick status-only update.
   * Maps to PUT /api/contracts/{id}/status?status=...
   */
  updateContractStatus(contractId: string, status: string): Observable<any> {
    const params = new HttpParams().set('status', status);
    return this.http.put<any>(
      `${this.apiUrl}/${contractId}/status`,
      null,
      { headers: this.getHeaders(), params }
    ).pipe(catchError(this.handleError));
  }

  // ==================== REMINDER ENDPOINTS ====================

  getContractReminders(contractNodeId: string): Observable<ReminderConfig[]> {
    return this.http.get<any>(`${this.reminderApiUrl}/contract/${contractNodeId}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => response.reminders || []),
      catchError(this.handleError)
    );
  }

  updateContractReminders(contractNodeId: string, reminders: ReminderConfig[]): Observable<any> {
    return this.http.put<any>(`${this.reminderApiUrl}/contract/${contractNodeId}`, reminders, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  addReminder(contractNodeId: string, reminder: ReminderConfig): Observable<any> {
    return this.http.post<any>(`${this.reminderApiUrl}/contract/${contractNodeId}`, reminder, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteReminder(contractNodeId: string, reminderId: number): Observable<any> {
    return this.http.delete<any>(`${this.reminderApiUrl}/contract/${contractNodeId}/${reminderId}`, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  getExpiringContracts(days: number = 30): Observable<any[]> {
    const params = new HttpParams().set('days', days.toString());
    return this.http.get<any>(`${this.reminderApiUrl}/expiring`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => response.contracts || []),
      catchError(this.handleError)
    );
  }

  getExpiredContracts(): Observable<any[]> {
    return this.http.get<any>(`${this.reminderApiUrl}/expired`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => response.contracts || []),
      catchError(this.handleError)
    );
  }

  getReminderDashboard(): Observable<any> {
    return this.http.get<any>(`${this.reminderApiUrl}/dashboard`, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  getExpiryDashboard(): Observable<ExpiryDashboard> {
    return this.http.get<any>(`${this.reminderApiUrl}/expiry-dashboard`, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  triggerExpirationCheck(): Observable<any> {
    return this.http.post<any>(`${this.reminderApiUrl}/trigger/expiration-check`, null, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  triggerReminderProcessing(): Observable<any> {
    return this.http.post<any>(`${this.reminderApiUrl}/trigger/reminder-processing`, null, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  sendTestNotification(contractNodeId: string, email: string): Observable<any> {
    const params = new HttpParams().set('email', email);
    return this.http.post<any>(`${this.reminderApiUrl}/test/${contractNodeId}`, null, {
      headers: this.getHeaders(),
      params
    }).pipe(catchError(this.handleError));
  }

  // ==================== UTILITY METHODS ====================

  createBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  revokeBlobUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = this.createBlobUrl(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    this.revokeBlobUrl(url);
  }

  calculateDaysBetween(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Generate default expiry reminders for a contract.
   * @param expiryDate    ISO date string (e.g. "2026-12-31")
   * @param milestoneDays Array of days-before-expiry to create reminders for (e.g. [90, 30, 7])
   */
  generateDefaultExpiryReminders(expiryDate: string, milestoneDays: number[] = [90, 30, 7]): ReminderConfig[] {
    const reminders: ReminderConfig[] = [];
    const expiry = new Date(expiryDate);
    const today = new Date();
    milestoneDays.forEach((days, index) => {
      const reminderDate = new Date(expiry);
      reminderDate.setDate(reminderDate.getDate() - days);
      if (reminderDate > today) {
        reminders.push({
          id: index + 1,
          reminderType: 'EXPIRY',
          purpose: `Contract expiring in ${days} days`,
          reminderDate: reminderDate.toISOString().split('T')[0],
          daysBefore: days,
          priority: days <= 7 ? 'CRITICAL' : days <= 30 ? 'HIGH' : 'MEDIUM',
          notifyOwner: true,
          notifyReviewers: true,
          notifyApprovers: true,
          notifyDepartment: true,
          recipients: [],
          sent: false
        });
      }
    });
    return reminders;
  }

  downloadPdfByNodeId(nodeId: string): Observable<Blob> {
  const url = `${environment.alfrescoUrl}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?attachment=false`;
  return this.http.get(url, {
    headers: this.getAuthHeadersOnly(),
    responseType: 'blob'
  });
}

  healthCheck(): Observable<any> {
    return this.http.get(`${this.apiUrl}/health`)
      .pipe(catchError(this.handleError));
  }

  /**
 * Fetch raw content of any Alfresco node by nodeId.
 * Goes through nginx → Alfresco using the Keycloak Bearer token.
 * Use this to load PDFs for iframe display (blob URL pattern).
 */
downloadAlfrescoNode(nodeId: string): Observable<Blob> {
  const alfrescoBase = environment.alfrescoUrl;
  const url = `${alfrescoBase}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?attachment=false`;
  return this.http.get(url, {
    headers: this.getAuthHeadersOnly(),
    responseType: 'blob'
  }).pipe(catchError(this.handleError));
}

  // ==================== EXECUTED CONTRACT UPLOAD ====================

  /**
   * Upload a contract that was signed externally (skips the review workflow).
   * Posts multipart/form-data to POST /api/contracts/upload-executed.
   * The backend forces contractStatus = "Active" and records contractOrigin = "EXECUTED".
   *
   * @param file      The signed contract file (PDF, DOCX, or image)
   * @param metadata  Full ContractMetadataUpdateRequest payload as a plain object
   */
  uploadExecutedContract(
    file: File,
    metadata: Record<string, any>
  ): Observable<{ success: boolean; nodeId?: string; contractNumber?: string; error?: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('metadata', JSON.stringify({ ...metadata, contractStatus: 'Active', convertToContractType: true }));

    return this.http.post<any>(
      `${this.apiUrl}/upload-executed`,
      formData,
      { headers: this.getAuthHeadersOnly() }  // No Content-Type header — browser sets multipart boundary
    ).pipe(catchError(this.handleError));
  }

  // ==================== ERROR HANDLING ====================

  private handleError(error: any): Observable<never> {
    console.error('ContractService Error:', error);
    let errorMessage = 'An error occurred';
    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.status) {
      errorMessage = `Error ${error.status}: ${error.statusText}`;
    }
    return throwError(() => new Error(errorMessage));
  }
} 